import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Archive, Trash2, RotateCcw, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PageHeading from "@/components/PageHeading";
import PedBoard from "@/components/PedBoard";

export default function Ped() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [plans, setPlans] = useState([]);
  const [archived, setArchived] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [newName, setNewName] = useState("");
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      if (data.length) setBrandId(data[0].id);
    }).catch(() => {});
  }, []);

  const loadPlans = useCallback(() => {
    if (!brandId) return;
    api.get("/ped/plans", { params: { brand_id: brandId, status: "active" } }).then(({ data }) => setPlans(data));
    api.get("/ped/plans", { params: { brand_id: brandId, status: "archived" } }).then(({ data }) => setArchived(data));
  }, [brandId]);
  useEffect(() => { setActivePlan(null); loadPlans(); }, [loadPlans]);

  const createPlan = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/ped/plans", { brand_id: brandId, name: newName.trim() });
      setNewName("");
      loadPlans();
      setActivePlan(data);
      toast.success("Piano creato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const archivePlan = async (p) => {
    await api.patch(`/ped/plans/${p.id}`, { status: "archived" });
    toast.success("Piano archiviato");
    if (activePlan?.id === p.id) setActivePlan(null);
    loadPlans();
  };

  const restorePlan = async (p) => {
    await api.patch(`/ped/plans/${p.id}`, { status: "active" });
    loadPlans();
  };

  const deletePlan = async (p) => {
    if (!window.confirm(`Eliminare definitivamente il piano "${p.name}"?`)) return;
    await api.delete(`/ped/plans/${p.id}`);
    toast.success("Piano eliminato");
    loadPlans();
  };

  const duplicatePlan = async (p) => {
    try {
      await api.post(`/ped/plans/${p.id}/duplicate`, {});
      toast.success("Piano duplicato come nuova bozza");
      loadPlans();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (activePlan) {
    return (
      <div data-testid="ped-page">
        <PedBoard plan={activePlan} onBack={() => { setActivePlan(null); loadPlans(); }} />
      </div>
    );
  }

  return (
    <div data-testid="ped-page">
      <PageHeading kicker="05 · CONTENUTI" title="Piano Editoriale"
        subtitle="Schede di contenuto trascinabili. Salva come bozza o pubblica sul calendario." />

      <div className="flex flex-wrap items-center gap-4 mb-10">
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="ped-brand-select" className="field-line max-w-[220px] bg-transparent">
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div className="flex items-center gap-3">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome nuovo piano (es. Settembre 2026)" data-testid="new-plan-name" className="field-line w-64" />
          <button onClick={createPlan} data-testid="create-plan-button" className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors">
            <Plus size={14} strokeWidth={1.5} /> Crea piano
          </button>
        </div>
      </div>

      {plans.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-10 text-center">Nessun piano attivo per questo brand.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {plans.map((p) => (
            <div key={p.id} onClick={() => setActivePlan(p)} data-testid={`plan-card-${p.id}`} className="group bg-white/60 border border-white hover:border-black/40 transition-colors p-6 cursor-pointer">
              <div className="flex items-start justify-between">
                <h3 className="font-display text-2xl text-ink">{p.name}</h3>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); duplicatePlan(p); }} data-testid={`duplicate-plan-${p.id}`} title="Duplica"
                    className="text-stone2e hover:text-ink"><Copy size={16} strokeWidth={1.5} /></button>
                  <button onClick={(e) => { e.stopPropagation(); archivePlan(p); }} data-testid={`archive-plan-${p.id}`} title="Archivia"
                    className="text-stone2e hover:text-ink"><Archive size={16} strokeWidth={1.5} /></button>
                </div>
              </div>
              <p className="meta-label mt-3">{p.items_count} CONTENUTI</p>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setShowArchive(!showArchive)} data-testid="toggle-archive-button" className="meta-label link-underline">
        ARCHIVIO PIANI ({archived.length})
      </button>
      {showArchive && (
        <div className="mt-5 space-y-px bg-black/10 border border-black/10 max-w-xl">
          {archived.length === 0 && <p className="bg-greige/60 p-4 font-editorial italic text-stone2e">Nessun piano archiviato.</p>}
          {archived.map((p) => (
            <div key={p.id} className="bg-greige/60 p-4 flex items-center justify-between gap-3" data-testid={`archived-plan-${p.id}`}>
              <div>
                <p className="font-body text-sm text-ink">{p.name}</p>
                <p className="meta-label mt-0.5">{p.items_count} CONTENUTI</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => duplicatePlan(p)} data-testid={`duplicate-plan-${p.id}`} title="Duplica" className="text-stone2e hover:text-ink"><Copy size={15} strokeWidth={1.5} /></button>
                <button onClick={() => restorePlan(p)} data-testid={`restore-plan-${p.id}`} title="Ripristina" className="text-stone2e hover:text-ink"><RotateCcw size={15} strokeWidth={1.5} /></button>
                <button onClick={() => deletePlan(p)} data-testid={`delete-plan-${p.id}`} title="Elimina" className="text-stone2e hover:text-ink"><Trash2 size={15} strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
